import React, { useState } from 'react';
import ElectronicPrescriptionPolandAddMedicamentForm, {
	ElectronicPrescriptionPolandFormValues,
} from './ElectronicPrescriptionPolandAddMedicamentForm';
// @ts-ignore
import styles from './ElectronicPrescriptionPoland.module.scss';
import ElectronicPrescriptionPolandMedicationsList from './ElectronicPrescriptionPolandMedicationsList';
import { useEffectAsync } from '../../../../helpers';
import Loading from '../../../../Components/Loading';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import {
	CreateP1PrescriptionMedicamentsInput,
	useCreateP1PrescriptionMutation,
} from '../../../../graphql/graphql';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useFilters from '../../../Base/Hooks/useFilters';
import apiClient from '../../../../Services/ApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import Text from '../../../../Components/Base/Text/Text';

interface Props {
	patientId: string;
	onCreate?: () => void;
}

const ElectronicPrescriptionPoland: React.FC<Props> = ({
	patientId,
	onCreate,
}) => {
	const [medications, setMedications] = useState<
		ElectronicPrescriptionPolandFormValues[]
	>([]);
	const [isLoading, setIsLoading] = useState(false);
	const { displayGQLError, displayError } = useApiClient();
	const { handleDateValue } = useFilters();
	const { showNotificationSuccess } = useNotification();
	const [isPatientValid, setIsPatientValid] = useState(false);
	const [createPrescription] = useCreateP1PrescriptionMutation();

	const handleRemoveMedicament = (id: string) => {
		setMedications(prev => prev.filter(item => item.id !== id));
	};

	const handleAddMedicament = (
		values: ElectronicPrescriptionPolandFormValues,
	) => {
		setMedications(prev => {
			return [...prev, values];
		});
	};

	const transformMedications = () => {
		const temp: CreateP1PrescriptionMedicamentsInput[] = [];
		medications.forEach(item => {
			const {
				medicament,
				paymentCoverage,
				validFrom,
				id,
				medicamentChange,
				dosageInterval,
				dosageIntervalCustom,
				recipeRepeat,
				...rest
			} = item;

			const isCustom: boolean = dosageInterval.value === 'custom';

			temp.push({
				...rest,
				id: medicament.value as string,
				paymentCoverage: paymentCoverage.value as string,
				validFrom: handleDateValue(validFrom),
				medicamentChange: !medicamentChange,
				dosageInterval: isCustom
					? dosageIntervalCustom!
					: parseInt(dosageInterval.value as string),
				recipeRepeat: recipeRepeat || 0,
			});
		});
		return temp;
	};

	const handleIssuePrescription = async () => {
		setIsLoading(true);
		const result = await createPrescription({
			input: {
				patientId: patientId,
				prescriptionMedicaments: transformMedications(),
			},
		});
		displayGQLError(result);
		if ('data' in result) {
			showNotificationSuccess({ content: 'base.ePrescriptionCreated' });
			onCreate?.();
		}
		setIsLoading(false);
	};

	useEffectAsync(async () => {
		setIsLoading(true);
		try {
			const response = await apiClient.get(`/p1/erecept/validate/${patientId}`);
			setIsPatientValid(response.status === 200);
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	}, []);

	if (isLoading) {
		return <Loading />;
	}

	if (!isPatientValid) {
		return (
			<div className={styles.wrapper}>
				<Text
					className={styles.invalidPatientText}
					tx='component.electronicPrescriptionPoland.invalidPatientData'
				/>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<ElectronicPrescriptionPolandAddMedicamentForm
				onSubmit={handleAddMedicament}
			/>
			<ElectronicPrescriptionPolandMedicationsList
				medications={medications}
				onRemove={handleRemoveMedicament}
			/>
			{medications.length > 0 && (
				<div className={styles.controls}>
					<Button
						theme={Theme.GREEN}
						title='component.electronicPrescriptionPoland.issuePrescription'
						onClick={handleIssuePrescription}
					/>
				</div>
			)}
		</div>
	);
};

export default ElectronicPrescriptionPoland;
